import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import {
  CapacitorSQLite,
  capSQLiteChanges,
  capTask,
  SQLiteConnection,
  SQLiteDBConnection,
} from '@capacitor-community/sqlite';
import { BehaviorSubject } from 'rxjs';
import { OFFLINE_DATABASE_MIGRATIONS, OFFLINE_DATABASE_VERSION } from './offline-database.migrations';
import { OfflineDatabaseStatus } from './offline-database.models';

@Injectable({ providedIn: 'root' })
export class OfflineDatabaseService {
  private readonly sqlite = new SQLiteConnection(CapacitorSQLite);
  private readonly statusSubject = new BehaviorSubject<OfflineDatabaseStatus>({
    state: Capacitor.isNativePlatform() ? 'fechado' : 'indisponivel',
    userId: 0,
    databaseName: '',
  });
  private connection: SQLiteDBConnection | null = null;
  private activeUserId = 0;
  private databaseName = '';
  private lifecycle: Promise<void> = Promise.resolve();

  readonly status$ = this.statusSubject.asObservable();

  get status(): OfflineDatabaseStatus {
    return this.statusSubject.value;
  }

  get isSupported(): boolean {
    return Capacitor.isNativePlatform();
  }

  get isReady(): boolean {
    return !!this.connection && this.status.state === 'pronto';
  }

  get userId(): number {
    return this.activeUserId;
  }

  initializeForUser(userId: number): Promise<boolean> {
    const normalizedUserId = Math.trunc(Number(userId || 0));
    return this.runLifecycle(async () => {
      if (!this.isSupported || normalizedUserId <= 0) {
        await this.closeCurrentConnection();
        this.publishStatus(this.isSupported ? 'fechado' : 'indisponivel', 0, '');
        return false;
      }

      if (this.connection && this.activeUserId === normalizedUserId && this.status.state === 'pronto') {
        return true;
      }

      await this.closeCurrentConnection();
      const databaseName = `construfacil_offline_user_${normalizedUserId}`;
      this.publishStatus('inicializando', normalizedUserId, databaseName);

      try {
        await this.sqlite.checkConnectionsConsistency().catch(() => ({ result: false }));
        await this.sqlite.addUpgradeStatement(databaseName, OFFLINE_DATABASE_MIGRATIONS);

        const existingConnection = await this.sqlite.isConnection(databaseName, false);
        this.connection = existingConnection.result
          ? await this.sqlite.retrieveConnection(databaseName, false)
          : await this.sqlite.createConnection(databaseName, false, 'no-encryption', OFFLINE_DATABASE_VERSION, false);

        const isOpen = await this.connection.isDBOpen();
        if (!isOpen.result) {
          await this.connection.open();
        }

        await this.connection.execute('PRAGMA foreign_keys = ON; PRAGMA busy_timeout = 5000;', false);

        const now = new Date().toISOString();
        await this.connection.run(
          `INSERT INTO app_metadata (key, value, updated_at)
           VALUES (?, ?, ?)
           ON CONFLICT(key) DO UPDATE SET value = excluded.value, updated_at = excluded.updated_at`,
          ['account_user_id', String(normalizedUserId), now],
        );
        await this.connection.run(
          `INSERT INTO app_metadata (key, value, updated_at)
           VALUES (?, ?, ?)
           ON CONFLICT(key) DO UPDATE SET value = excluded.value, updated_at = excluded.updated_at`,
          ['schema_version', String(OFFLINE_DATABASE_VERSION), now],
        );

        this.activeUserId = normalizedUserId;
        this.databaseName = databaseName;
        this.publishStatus('pronto', normalizedUserId, databaseName);
        return true;
      } catch (error) {
        const message = this.errorMessage(error);
        await this.closeCurrentConnection();
        this.statusSubject.next({
          state: 'erro',
          userId: normalizedUserId,
          databaseName,
          error: message,
        });
        return false;
      }
    });
  }

  close(): Promise<void> {
    return this.runLifecycle(async () => {
      await this.closeCurrentConnection();
      this.publishStatus(this.isSupported ? 'fechado' : 'indisponivel', 0, '');
    });
  }

  deleteForUser(userId: number): Promise<void> {
    const normalizedUserId = Math.trunc(Number(userId || 0));
    return this.runLifecycle(async () => {
      if (!this.isSupported || normalizedUserId <= 0) return;

      const databaseName = `construfacil_offline_user_${normalizedUserId}`;
      if (this.activeUserId === normalizedUserId || this.databaseName === databaseName) {
        await this.closeCurrentConnection();
      } else {
        const existingConnection = await this.sqlite.isConnection(databaseName, false).catch(() => ({ result: false }));
        if (existingConnection.result) {
          await this.sqlite.closeConnection(databaseName, false).catch(() => undefined);
        }
      }

      await CapacitorSQLite.deleteDatabase({ database: databaseName });
      this.publishStatus('fechado', 0, '');
    });
  }

  async query<T extends Record<string, unknown>>(statement: string, values: unknown[] = []): Promise<T[]> {
    const connection = this.requireConnection();
    const result = await connection.query(statement, values as any[]);
    return (result.values || []) as T[];
  }

  async run(
    statement: string,
    values: unknown[] = [],
    transaction = true,
  ): Promise<capSQLiteChanges> {
    return this.requireConnection().run(statement, values as any[], transaction);
  }

  async execute(statements: string, transaction = true): Promise<capSQLiteChanges> {
    return this.requireConnection().execute(statements, transaction);
  }

  async executeTransaction(tasks: capTask[]): Promise<capSQLiteChanges> {
    return this.requireConnection().executeTransaction(tasks);
  }

  private requireConnection(): SQLiteDBConnection {
    if (!this.connection || this.status.state !== 'pronto') {
      throw new Error('O banco offline ainda não está pronto para uso.');
    }
    return this.connection;
  }

  private async closeCurrentConnection(): Promise<void> {
    const name = this.databaseName || this.connection?.getConnectionDBName() || '';
    this.connection = null;
    this.activeUserId = 0;
    this.databaseName = '';

    if (!name) return;
    await this.sqlite.closeConnection(name, false).catch(() => undefined);
  }

  private runLifecycle<T>(work: () => Promise<T>): Promise<T> {
    const result = this.lifecycle.then(work, work);
    this.lifecycle = result.then(() => undefined, () => undefined);
    return result;
  }

  private publishStatus(
    state: OfflineDatabaseStatus['state'],
    userId: number,
    databaseName: string,
  ): void {
    this.statusSubject.next({ state, userId, databaseName });
  }

  private errorMessage(error: unknown): string {
    if (error instanceof Error) return error.message;
    if (typeof error === 'string') return error;
    try {
      return JSON.stringify(error);
    } catch {
      return 'Falha desconhecida ao inicializar o banco offline.';
    }
  }
}
